/**
 * Assessment API – fetch assessment definitions from the backend.
 * Used by the web app to load full/basic (and custom) assessments from the database.
 */

const getAuthHeaders = () => {
  const token =
    localStorage.getItem('token') || sessionStorage.getItem('token');
  return token ? { 'x-auth-token': token } : {};
};

// Align with rest of app: REACT_APP_API_URL + /api. In dev, fallback to port 5000 so
// API calls always hit the backend even when the env var isn't loaded.
const getApiBase = () => {
  const host = process.env.REACT_APP_API_URL;
  if (host) return `${host}/api`;
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost' &&
    window.location.port === '3000'
  ) {
    return 'http://localhost:5000/api';
  }
  return '/api';
};

/**
 * Fetch system assessment by type (full | basic). No auth required.
 * @param {string} type - 'full' or 'basic'
 * @returns {Promise<Object>} Assessment document with questions array
 */
export const fetchAssessmentByType = async (type) => {
  const url = `${getApiBase()}/assessments?type=${encodeURIComponent(type)}`;
  const res = await fetch(url);
  if (!res.ok) {
    const err = new Error(res.statusText || 'Failed to load assessment');
    err.status = res.status;
    err.body = await res.json().catch(() => ({}));
    throw err;
  }
  return res.json();
};

/**
 * Fetch assessment by id (used for custom assessments). Requires auth.
 * @param {string} id - Assessment _id
 * @returns {Promise<Object>} Assessment document with questions array
 */
export const fetchAssessmentById = async (id) => {
  const url = `${getApiBase()}/assessments/${encodeURIComponent(id)}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    const err = new Error(res.statusText || 'Failed to load assessment');
    err.status = res.status;
    err.body = await res.json().catch(() => ({}));
    throw err;
  }
  return res.json();
};

/**
 * List current user's (trainer) custom assessments. Requires auth + trainer role.
 * @returns {Promise<Array>} List of { _id, name, description, questionCount, ... }
 */
export const fetchMyAssessments = async () => {
  const url = `${getApiBase()}/assessments/mine`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) {
    const err = new Error(res.statusText || 'Failed to load my assessments');
    err.status = res.status;
    err.body = await res.json().catch(() => ({}));
    throw err;
  }
  return res.json();
};

/**
 * Create custom assessment. Requires auth + trainer.
 * @param {Object} payload - { name, description?, questions }
 * @returns {Promise<Object>} Created assessment document
 */
export const createAssessment = async (payload) => {
  const url = `${getApiBase()}/assessments`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.error || res.statusText || 'Failed to create assessment');
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return res.json();
};

/**
 * Update custom assessment. Requires auth + trainer + owner.
 * @param {string} id - Assessment _id
 * @param {Object} payload - { name?, description?, questions? }
 * @returns {Promise<Object>} Updated assessment document
 */
export const updateAssessment = async (id, payload) => {
  const url = `${getApiBase()}/assessments/${encodeURIComponent(id)}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.error || res.statusText || 'Failed to update assessment');
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return res.json();
};

/**
 * Soft-delete (deactivate) custom assessment. Requires auth + trainer + owner.
 * @param {string} id - Assessment _id
 * @returns {Promise<Object>} { message, id }
 */
export const deleteAssessment = async (id) => {
  const url = `${getApiBase()}/assessments/${encodeURIComponent(id)}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.error || res.statusText || 'Failed to delete assessment');
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return res.json();
};
